import { forwardRef, useImperativeHandle, useRef } from 'react';
import { IconSearch, IconX } from '@tabler/icons-react';

import { cn } from '../tokens/cn';
import { Input, type InputProps } from '../primitives/input';
import { Spinner } from '../feedback/spinner';

export interface SearchInputProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (next: string) => void;
  /** Shows a spinner on the right edge while a search request is in flight. */
  isLoading?: boolean;
  /** When false, the clear button is hidden even if value is non-empty. */
  showClear?: boolean;
  /** Optional aria-label for the clear button — defaults to "Clear search". */
  clearLabel?: string;
}

/**
 * Design-system search box: left search icon, neutral clear button on the right
 * (replaced by a spinner while loading). Hides the browser-native search-cancel
 * button so the chrome stays consistent across browsers.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      isLoading = false,
      showClear = true,
      clearLabel = 'Clear search',
      size = 'sm',
      className,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const iconSize = size === 'lg' ? 18 : size === 'md' ? 16 : 14;
    const hasClear = showClear && value.length > 0 && !isLoading && !disabled;
    const showSpinner = isLoading;

    return (
      <div className="relative">
        <IconSearch
          size={iconSize}
          aria-hidden
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tk-surface-fg-muted)] pointer-events-none"
        />
        <Input
          ref={inputRef}
          size={size}
          type="search"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-busy={isLoading}
          className={cn(
            'pl-9',
            (hasClear || showSpinner) && 'pr-9',
            '[&::-webkit-search-cancel-button]:appearance-none',
            '[&::-webkit-search-decoration]:appearance-none',
            className,
          )}
          {...rest}
        />
        {showSpinner && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center pointer-events-none">
            <Spinner size="xs" tone="muted" />
          </span>
        )}
        {hasClear && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              inputRef.current?.focus();
            }}
            aria-label={clearLabel}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2',
              'inline-flex h-6 w-6 items-center justify-center rounded-tk-full',
              'text-[var(--tk-surface-fg-muted)] hover:text-[var(--tk-surface-fg)]',
              'hover:bg-[var(--tk-surface-bg)] transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tk-brand-ring)]',
            )}
          >
            <IconX size={14} />
          </button>
        )}
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';
